import React from 'react';
import { TylerState, updateReact, useTylerStateUpdate } from '../../utils/TylerState';
import { CashFlowItem, CURRENCY_SYMBOLS } from '../../types/personalForecast';
import { addMonths, monthsBetween, latestActualMonth } from '../../utils/forecastEngine';

/**
 * What each cash flow actually moved in the recorded months: one row per item, one column per
 * history month. The forecast rules take over in the month after the latest recorded balance.
 */
const CashFlowHistory: React.FC = () => {
  useTylerStateUpdate();
  const { accounts, cashFlows, settings } = TylerState.forecast;
  const start = settings.historyStartMonth;
  const end = latestActualMonth(accounts);
  const months = start && end && start <= end ? Array.from({ length: monthsBetween(start, end) }, (_, i) => addMonths(start, i)) : [];

  const setActual = (cf: CashFlowItem, month: string, raw: string) => {
    const actuals = { ...(cf.actuals || {}) };
    if (raw.trim() === '') delete actuals[month];
    else {
      const value = parseFloat(raw);
      if (Number.isNaN(value)) return;
      actuals[month] = value;
    }
    cf.actuals = actuals;
    TylerState.forecast.isDirty = true;
    updateReact();
  };

  const monthLabel = (m: string) => new Date(m + '-01T00:00:00').toLocaleDateString('en-CA', { month: 'short', year: '2-digit' });
  const currencyOf = (cf: CashFlowItem) => accounts.find(a => a.id === cf.accountId)?.currency || settings.baseCurrency;
  const money = (v: number, cur = settings.baseCurrency) => `${v < 0 ? '-' : ''}${CURRENCY_SYMBOLS[cur]}${Math.abs(v).toLocaleString('en-CA', { maximumFractionDigits: 0 })}`;
  const total = (cf: CashFlowItem) => months.reduce((sum, m) => sum + (cf.actuals?.[m] || 0), 0);

  if (cashFlows.length === 0 || months.length === 0) return null;

  const section = (label: string, list: CashFlowItem[], color?: string) => list.length === 0 ? null : (
    <>
      <tr><td colSpan={months.length + 2} style={{ fontWeight: 700, color, background: '#f8fafc' }}>{label}</td></tr>
      {list.map(cf => (
        <tr key={cf.id}>
          <td className="name-cell" style={{ paddingLeft: '1.5rem' }} title={cf.name}>{cf.name || '(unnamed)'}</td>
          {months.map(m => (
            <td key={m}>
              <input
                type="number"
                value={cf.actuals?.[m] ?? ''}
                placeholder="0"
                onChange={e => setActual(cf, m, e.target.value)}
                aria-label={`${cf.name} ${monthLabel(m)}`}
                style={{ width: '84px', padding: '3px 5px', border: '1px solid #e2e8f0', borderRadius: '4px', textAlign: 'right' }}
              />
            </td>
          ))}
          <td className="nowrap" style={{ textAlign: 'right', fontWeight: 600, color }}>{money(total(cf), currencyOf(cf))}</td>
        </tr>
      ))}
    </>
  );

  return (
    <div className="account-section" style={{ marginTop: '2.5rem' }}>
      <h3 style={{ margin: '0 0 0.25rem' }}>Cash Flow History</h3>
      <p className="help-text" style={{ marginTop: 0 }}>
        What each item actually moved in your recorded months ({monthLabel(months[0])} to {monthLabel(months[months.length - 1])}).
        These feed the history columns of the Cash Flow views; the forecast rules take over from {monthLabel(addMonths(end!, 1))}. Blank means nothing recorded.
      </p>
      <div className="frozen-table-wrap">
        <table className="accounts-table compact frozen-table history-grid">
          <thead>
            <tr>
              <th>Item</th>
              {months.map(m => <th key={m} style={{ textAlign: 'right' }}>{monthLabel(m)}</th>)}
              <th style={{ textAlign: 'right' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {section('Income', cashFlows.filter(cf => cf.isInflow && !cf.toAccountId), '#059669')}
            {section('Spending and transfers', cashFlows.filter(cf => !cf.isInflow || cf.toAccountId), '#dc2626')}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CashFlowHistory;
